import type { Resource } from "@core/shared/types";
import { trans } from "@mongez/localization";
import type { ReactNode } from "react";

/**
 * Get the remaining views of the given resource, or `null` if no view limit applies to it
 */
export function getResourceRemainingViews(resource?: Resource | null) {
  if (!resource?.maxViews || resource.maxViews <= 0) return null;

  if (typeof resource.remainingViews === "number") {
    return Math.max(0, resource.remainingViews);
  }

  return Math.max(0, resource.maxViews - (resource.viewsCount || 0));
}

/**
 * Remaining views hint text, i.e "فاضلك 3 مشاهدات"
 */
export function getRemainingViewsText(remaining: number): ReactNode {
  if (remaining <= 0) return trans("grades.lastViewInProgress");
  if (remaining === 1) return trans("grades.remainingViewsOne");
  if (remaining === 2) return trans("grades.remainingViewsTwo");
  if (remaining <= 10) {
    return trans("grades.remainingViewsFew", { views: remaining });
  }

  return trans("grades.remainingViewsMany", { views: remaining });
}

export function isViewLimitReached(resource?: Resource | null) {
  return Boolean(
    resource?.isLocked && resource.lockReason === "reachedMaxViews",
  );
}

/**
 * Title of a resource that is locked because the student reached its max views
 */
export function getViewLimitReachedTitle(resource: Resource): ReactNode {
  const maxViews = Number(resource.maxViews);

  if (!maxViews || maxViews <= 0) {
    return trans("grades.viewLimitReachedTitle");
  }

  const used = Math.min(Number(resource.viewsCount) || maxViews, maxViews);

  return trans("grades.viewLimitReachedTitleWithCount", {
    used,
    max: maxViews,
  });
}

/**
 * Short label shown on the collapsed resource card when it is locked
 */
export function getLockedShortLabel(resource: Resource): ReactNode {
  return isViewLimitReached(resource)
    ? trans("grades.viewLimitReachedShort")
    : trans("grades.locked");
}
